/**
 * useDashboardUIState Hook
 *
 * Manages UI state for the dashboard including modals, menus,
 * error banners, and refresh functionality.
 */

import { useState, useCallback } from 'react';
import { useRevalidator } from 'react-router';
import { useToastHelpers } from '../../components/feedback/ToastProvider';

/**
 * Return type for useDashboardUIState hook
 */
export interface UseDashboardUIStateReturn {
  // Modal state
  /** Whether add product modal is open */
  addProductModalOpen: boolean;
  /** Set add product modal open state */
  setAddProductModalOpen: (value: boolean) => void;

  // Search overlay state
  /** Whether search overlay is expanded */
  isSearchExpanded: boolean;
  /** Set search expanded state */
  setIsSearchExpanded: (value: boolean) => void;

  // Actions menu state
  /** Whether actions menu is open */
  isActionsMenuOpen: boolean;
  /** Set actions menu open state */
  setIsActionsMenuOpen: (value: boolean) => void;

  // Error banner
  /** Current error message to display in banner */
  errorBanner: string | null;
  /** Set error banner message */
  setErrorBanner: (value: string | null) => void;

  // Refresh functionality
  /** Whether dashboard is currently refreshing */
  isRefreshing: boolean;
  /** Handler to trigger dashboard data refresh */
  handleRefresh: () => Promise<void>;
}

/**
 * Hook for managing dashboard UI state
 *
 * Consolidates modal, menu, error banner, and refresh state
 * into a single hook for cleaner component code.
 *
 * @returns UI state and handlers
 */
export function useDashboardUIState(): UseDashboardUIStateReturn {
  const revalidator = useRevalidator();
  const toast = useToastHelpers();

  // Modal and menu state
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  // Error banner state
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  // Refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Manual refresh handler
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      revalidator.revalidate();
      toast.success('Alerts data refreshed');
    } catch {
      // Refresh error - silent fail, user can retry
      setIsRefreshing(false);
    }
  }, [revalidator, toast]);

  return {
    // Modal state
    addProductModalOpen,
    setAddProductModalOpen,

    // Search overlay state
    isSearchExpanded,
    setIsSearchExpanded,

    // Actions menu state
    isActionsMenuOpen,
    setIsActionsMenuOpen,

    // Error banner
    errorBanner,
    setErrorBanner,

    // Refresh
    isRefreshing,
    handleRefresh,
  };
}
